<script setup lang="ts">
import AppSidebar from "@/components/AppSidebar.vue"
import { Separator } from "@/components/ui/separator"
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar"
import { computed, ref, watch } from "vue"
import { useRestaurantStore } from "@/stores/restaurant"
import { useAuthStore } from "@/stores/auth"
import { useNetworkStatus } from "@/composables/useNetworkStatus"
import { useI18n } from "@/composables/useI18n"
import TrialExpiredModal from "@/components/TrialExpiredModal.vue"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { ENABLE_ONBOARDING_WIZARD } from "@/config/featureFlags"
import { UserCheck } from "lucide-vue-next"

const restaurantStore = useRestaurantStore()
const authStore = useAuthStore()
const { connectionStatus } = useNetworkStatus()
const { t } = useI18n()

const trialExpiredDismissed = ref(false)

const canDismissTrialModal = computed(() => {
  const role = authStore.user?.role
  return role === "admin" || role === "superadmin"
})

const showTrialExpired = computed(() => {
  const restaurant = restaurantStore.restaurant
  if (!restaurant || !restaurant.isTrialAccount || !restaurant.trialEndsAt) {
    return false
  }
  const ends = new Date(restaurant.trialEndsAt)
  const now = new Date()
  return ends.getTime() <= now.getTime()
})

const showTrialExpiredModal = computed(() => {
  if (!showTrialExpired.value) return false
  if (!canDismissTrialModal.value) return true
  return !trialExpiredDismissed.value
})

const statusConfig = computed(() => {
  switch (connectionStatus.value) {
    case "online":
      return {
        color: "bg-green-500",
        text: t("common.network.status.online"),
      }
    case "unstable":
      return {
        color: "bg-orange-500",
        text: t("common.network.status.unstable"),
      }
    case "offline":
      return {
        color: "bg-red-500",
        text: t("common.network.status.offline"),
      }
    default:
      return {
        color: "bg-gray-500",
        text: t("common.network.status.unknown"),
      }
  }
})

const trialLabel = computed(() => {
  const restaurant = restaurantStore.restaurant
  if (!restaurant?.isTrialAccount || !restaurant.trialEndsAt) return null
  const ends = new Date(restaurant.trialEndsAt)
  const now = new Date()
  const diffMs = ends.getTime() - now.getTime()
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays <= 0) return t("common.restaurant.trialExpired")
  return t("common.restaurant.trialDaysLeft", { days: diffDays })
})

const showOnboardingInfoModal = computed(() => {
  const restaurant = restaurantStore.restaurant
  const userRole = authStore.user?.role
  if (!restaurant || userRole !== "restaurant_owner") return false
  if (ENABLE_ONBOARDING_WIZARD) return false
  return restaurant.onboardingCompleted === false
})

const isOnboardingInfoOpen = ref(false)

if (showOnboardingInfoModal.value) {
  isOnboardingInfoOpen.value = true
}

watch(
  () => isOnboardingInfoOpen.value,
  async newVal => {
    if (!newVal && showOnboardingInfoModal.value) {
      const currentRestaurant = restaurantStore.restaurant
      if (currentRestaurant?.id && currentRestaurant.onboardingCompleted === false) {
        try {
          await restaurantStore.updateRestaurant(currentRestaurant.id, {
            onboardingCompleted: true,
          })
        } catch (error) {
          console.error("Failed to mark onboarding as completed", error)
        }
      }
    }
  }
)

</script>

<template>
  <SidebarProvider>
    <AppSidebar />
    <SidebarInset>
      <header
        class="flex h-16 shrink-0 items-center justify-between gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12"
      >
        <div class="flex items-center gap-2 px-4">
          <SidebarTrigger class="-ml-1" />
          <Separator orientation="vertical" class="mr-2 h-4 hidden md:block" />
          <!-- <Breadcrumb class="hidden md:block">
            <BreadcrumbList>
              <template v-for="(item, index) in breadcrumbItems" :key="item.href">
                <BreadcrumbItem>
                  <BreadcrumbLink v-if="!item.isLast && item.href !== '#'">
                    <router-link :to="item.href">
                      {{ item.title }}
                    </router-link>
                  </BreadcrumbLink>
                  <BreadcrumbPage v-else-if="item.isLast">
                    {{ item.title }}
                  </BreadcrumbPage>
                  <span v-else class="text-muted-foreground">
                    {{ item.title }}
                  </span>
                </BreadcrumbItem>
                <BreadcrumbSeparator
                  v-if="index < breadcrumbItems.length - 1"
                  class="hidden md:block"
                />
              </template>
            </BreadcrumbList>
          </Breadcrumb> -->
        </div>
        <div class="flex items-center gap-2 px-4">
          <span
            v-if="trialLabel"
            class="text-sm font-medium px-2.5 py-1 rounded-md border"
            :class="
              showTrialExpired
                ? 'text-destructive bg-destructive/10 border-destructive/20'
                : 'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/20 border-amber-500/30 dark:border-amber-500/40'
            "
          >
            {{ trialLabel }}
          </span>
          <div class="flex items-center gap-2 text-sm text-muted-foreground">
            <div
              class="h-2 w-2 rounded-full relative"
              :class="[statusConfig.color, connectionStatus === 'online' ? 'animate-pulse' : '']"
            >
              <div
                v-if="connectionStatus === 'online'"
                class="absolute inset-0 h-2 w-2 rounded-full animate-ping"
                :class="statusConfig.color"
                style="opacity: 0.75"
              />
            </div>
            <span>{{ statusConfig.text }}</span>
          </div>
        </div>
      </header>
      <div class="flex flex-1 flex-col gap-4 p-4 pt-0">
        <RouterView />
      </div>
      <TrialExpiredModal
        :open="showTrialExpiredModal"
        :can-dismiss="canDismissTrialModal"
        @close="trialExpiredDismissed = true"
      />
      <Dialog
        v-if="showOnboardingInfoModal"
        :open="isOnboardingInfoOpen"
        @update:open="val => (isOnboardingInfoOpen = val)"
      >
        <DialogContent class="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{{ t("common.onboarding.welcomeTitle") }}</DialogTitle>
            <DialogDescription>
              <p class="mb-3">
                Twoja restauracja została utworzona. Aby w pełni uruchomić DineCloud, wykonaj
                poniższe kroki:
              </p>
              <ol class="list-decimal list-inside space-y-1 text-sm">
                <li>
                  Dodaj menu w zakładce
                  <strong>Menu</strong>
                  (sekcje, produkty, ceny).
                </li>
                <li>
                  Utwórz stoliki i wygeneruj kody QR w zakładce
                  <strong>Stoliki</strong>
                  .
                </li>
                <li>
                  Jeśli korzystasz z planu
                  <strong>Pełnego</strong>
                  , skonfiguruj płatności online iMoje w
                  <strong>Ustawienia → Płatności</strong>
                  .
                </li>
                <li>Przetestuj cały flow – zeskanuj QR przy stoliku i złóż testowe zamówienie.</li>
              </ol>
            </DialogDescription>
          </DialogHeader>
          <div class="mt-4 rounded-lg border bg-muted/40 px-4 py-3 flex items-start gap-3">
            <UserCheck class="mt-0.5 h-5 w-5 text-primary shrink-0" />
            <p class="text-sm text-muted-foreground">
              {{
                t("common.onboarding.successCoachNote") ||
                "Nasz bezpłatny opiekun sukcesu skontaktuje się z Tobą w celu udzielenia Ci pomocy."
              }}
            </p>
          </div>
          <div class="mt-6 flex justify-end">
            <button
              type="button"
              class="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground"
              @click="isOnboardingInfoOpen = false"
            >
              Rozumiem
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </SidebarInset>
  </SidebarProvider>
</template>
